import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Shell from "../components/Shell";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  ArrowLeft, Heart, CheckCircle2, Lock, AlertTriangle, ShieldCheck, Sparkles,
} from "lucide-react";

const SEVERITY = {
  mild: { color: "#4CAF50", label: "Mild" },
  moderate: { color: "#FF8F00", label: "Moderate" },
  severe: { color: "#D32F2F", label: "Severe" },
};

const TYPE_BADGE = {
  organic: { color: "#4CAF50", label: "Organic" },
  chemical: { color: "#7B1FA2", label: "Chemical" },
  environmental: { color: "#4FC3F7", label: "Environmental" },
  preventive: { color: "#FFD54F", label: "Preventive" },
};

function RemedyCard({ remedy, idx }) {
  const type = TYPE_BADGE[remedy.treatment_type] || TYPE_BADGE.organic;
  return (
    <div
      className="glass-card p-4 fade-up"
      data-testid={`healing-remedy-${idx}`}
      style={{ background: "rgba(0,0,0,0.42)", border: `1px solid ${type.color}33`, borderRadius: 18 }}
    >
      <div className="flex items-start gap-3 mb-3">
        <span
          className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
          style={{ background: `${type.color}22`, border: `1px solid ${type.color}66`, color: type.color }}
        >
          {idx + 1}
        </span>
        <div className="flex-1">
          <h3 className="text-base font-semibold text-white">{remedy.title}</h3>
          <div className="flex items-center gap-2 mt-1">
            <span
              className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-bold"
              style={{ background: `${type.color}22`, color: type.color, border: `1px solid ${type.color}55` }}
            >
              {type.label}
            </span>
            {remedy.difficulty && (
              <span className="text-[10px] text-muted-soft">· {remedy.difficulty}</span>
            )}
            {remedy.timeline && (
              <span className="text-[10px] text-muted-soft">· {remedy.timeline}</span>
            )}
          </div>
        </div>
      </div>
      <div className="space-y-1.5 mb-2">
        {(remedy.steps || []).map((step, i) => (
          <div key={i} className="flex items-start gap-2 text-xs text-soft">
            <CheckCircle2 size={12} className="text-[#4CAF50] mt-0.5 flex-shrink-0" />
            <span>{step}</span>
          </div>
        ))}
      </div>
      {(remedy.products || []).length > 0 && (
        <div className="mt-2 pt-2 border-t border-white/5">
          <p className="text-[10px] uppercase tracking-wider text-muted-soft mb-1">Suggested products</p>
          <p className="text-xs text-soft">
            {(remedy.products || []).map((p) => p.name).filter(Boolean).join(" · ")}
          </p>
        </div>
      )}
    </div>
  );
}

export default function HealingDetailScreen() {
  const { diseaseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [disease, setDisease] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/healing/diseases/${diseaseId}`);
        setDisease(data);
      } catch (err) {
        console.error("[HealingDetail] failed:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [diseaseId]);

  if (loading) {
    return (
      <Shell section="remedies">
        <p className="text-soft text-sm py-10 text-center">Loading remedy…</p>
      </Shell>
    );
  }
  if (!disease) {
    return (
      <Shell section="remedies">
        <p className="text-soft text-sm py-10 text-center">Disease not found.</p>
      </Shell>
    );
  }

  const sev = SEVERITY[disease.severity_typical] || SEVERITY.mild;
  const isFree = user?.tier === "free";

  return (
    <Shell section="remedies">
      <button
        onClick={() => navigate("/healing")}
        className="flex items-center gap-1 text-[#4CAF50] text-sm mb-3 hover:underline"
        data-testid="healing-back"
      >
        <ArrowLeft size={14} /> Healing library
      </button>

      <div className="glass-card-strong p-5 mb-4 fade-up" data-testid="healing-detail-header">
        <div className="flex items-start gap-3 mb-3">
          <span
            className="inline-flex items-center justify-center w-12 h-12 rounded-full flex-shrink-0"
            style={{ background: `${sev.color}1f`, border: `1px solid ${sev.color}55`, boxShadow: `0 0 18px ${sev.color}33` }}
          >
            <Heart size={20} style={{ color: sev.color }} strokeWidth={1.7} />
          </span>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-shadow-green">{disease.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <span
                className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold"
                style={{ background: `${sev.color}22`, color: sev.color, border: `1px solid ${sev.color}55` }}
              >
                {sev.label}
              </span>
              <span className="text-[10px] uppercase tracking-wider text-muted-soft">
                {disease.category}
              </span>
            </div>
          </div>
        </div>
        <p className="text-sm text-soft leading-relaxed">{disease.description}</p>
      </div>

      <div className="glass-card p-4 mb-4 fade-up stagger-1" style={{ background: "rgba(0,0,0,0.42)" }}>
        <h3 className="text-[11px] uppercase tracking-wider text-muted-soft mb-2 flex items-center gap-1.5">
          <AlertTriangle size={12} className="text-[#FF8F00]" />
          Common symptoms
        </h3>
        <div className="space-y-1.5">
          {(disease.common_symptoms || []).map((s, i) => (
            <div key={i} className="flex items-start gap-2 text-sm text-soft">
              <span className="w-1 h-1 rounded-full bg-[#4CAF50] mt-2 flex-shrink-0" />
              <span>{s}</span>
            </div>
          ))}
        </div>
      </div>

      {(disease.common_plants || []).length > 0 && (
        <div className="glass-card p-4 mb-4 fade-up stagger-2" style={{ background: "rgba(0,0,0,0.42)" }}>
          <h3 className="text-[11px] uppercase tracking-wider text-muted-soft mb-2 flex items-center gap-1.5">
            <Sparkles size={12} className="text-[#4CAF50]" />
            Commonly affects
          </h3>
          <p className="text-sm text-soft">{disease.common_plants.join(" · ")}</p>
        </div>
      )}

      <div className="flex items-center gap-2 mt-6 mb-3 fade-up stagger-3">
        <ShieldCheck size={16} className="text-[#4CAF50]" />
        <h2 className="text-base font-bold text-white uppercase tracking-wider">
          Treatment plan ({disease.remedy_count || (disease.remedies || []).length || 5} steps)
        </h2>
      </div>

      {disease.remedies_locked ? (
        <div
          className="glass-card p-6 text-center fade-up"
          data-testid="healing-locked"
          style={{ background: "rgba(255, 213, 79, 0.08)", border: "1px solid rgba(255, 213, 79, 0.35)" }}
        >
          <Lock size={28} className="text-[#FFD54F] mx-auto mb-2" />
          <h3 className="text-base font-semibold mb-1">
            {disease.remedy_count || 5} science-backed remedies are waiting
          </h3>
          <p className="text-xs text-soft mb-3">
            Step-by-step plans, recommended products, and treatment timelines for {disease.name}.
          </p>
          <button
            onClick={() => navigate("/subscription")}
            className="btn-primary inline-flex items-center justify-center gap-2"
            data-testid="healing-unlock-btn"
          >
            <Sparkles size={14} /> Unlock with Premium
          </button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="healing-remedies-list">
          {(disease.remedies || []).map((r, idx) => (
            <RemedyCard key={idx} remedy={r} idx={idx} />
          ))}
        </div>
      )}
    </Shell>
  );
}
